import { useState, useEffect, useCallback } from 'react';
import {
  Zap, Play, Pause, RefreshCw, Send, Clock, Users, TrendingUp,
  MessageSquare, Activity, Settings2, User, Phone, ArrowRight,
  Eye, ChevronRight, Terminal, Plus, Edit, Trash2, Power, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  getOutreachStatus, toggleOutreachEngine, updateOutreachConfig,
  getOutreachLogs, getOutreachQueue, sendOutreachNow,
  getCommands, createCommand, updateCommand, toggleCommand, deleteCommand
} from '../lib/outreach-api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const INTENT_CONFIG = {
  first_touch: { label: 'First Touch', color: 'text-blue-400', bg: 'bg-blue-500/15' },
  follow_up: { label: 'Follow Up', color: 'text-amber-400', bg: 'bg-amber-500/15' },
  re_engage: { label: 'Re-engage', color: 'text-violet-400', bg: 'bg-violet-500/15' },
  continue_convo: { label: 'Continue', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  interest_detected: { label: 'Interested', color: 'text-red-400', bg: 'bg-red-500/15' },
};

const STATE_CONFIG = {
  new: { label: 'New', color: 'text-slate-400' },
  contacted: { label: 'Contacted', color: 'text-blue-400' },
  engaged: { label: 'Engaged', color: 'text-emerald-400' },
  interested: { label: 'Interested', color: 'text-amber-400' },
  follow_up: { label: 'Follow Up', color: 'text-violet-400' },
  cold: { label: 'Cold', color: 'text-slate-500' },
  converted: { label: 'Converted', color: 'text-primary' },
};

function formatTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const OutreachPage = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState({});
  const [sending, setSending] = useState(null);
  // Custom Commands state
  const [commands, setCommands] = useState([]);
  const [cmdDialogOpen, setCmdDialogOpen] = useState(false);
  const [editingCmd, setEditingCmd] = useState(null);
  const [cmdForm, setCmdForm] = useState({ instruction: '', mode: 'passive' });
  const [cmdSaving, setCmdSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, logsRes, queueRes, cmdsRes] = await Promise.all([
        getOutreachStatus(), getOutreachLogs(30), getOutreachQueue(), getCommands()
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data);
      setQueue(queueRes.data);
      setCommands(cmdsRes.data);
      setConfig({
        min_daily_leads: statusRes.data.timing?.min_daily_leads || 5,
        max_daily_leads: statusRes.data.timing?.max_daily_leads || 10,
        day_start_hour: statusRes.data.timing?.day_start_hour || 9,
        day_end_hour: statusRes.data.timing?.day_end_hour || 21,
      });
    } catch (err) {
      // Production: error logged silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 30s when engine is running
  useEffect(() => {
    if (!status?.engine_running) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [status?.engine_running, loadData]);

  const handleToggle = async (enabled) => {
    try {
      await toggleOutreachEngine(enabled);
      toast.success(enabled ? 'Outreach Engine started' : 'Outreach Engine stopped');
      loadData();
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const handleSendNow = async (leadId) => {
    setSending(leadId);
    try {
      const res = await sendOutreachNow(leadId);
      toast.success(`Message sent: "${res.data.message?.substring(0, 50)}..."`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(null);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateOutreachConfig(config);
      toast.success('Configuration updated');
      setConfigOpen(false);
      loadData();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  // ─── Custom Command Handlers ────────────────────
  const openNewCommand = () => {
    setEditingCmd(null);
    setCmdForm({ instruction: '', mode: 'passive' });
    setCmdDialogOpen(true);
  };

  const openEditCommand = (cmd) => {
    setEditingCmd(cmd);
    setCmdForm({ instruction: cmd.instruction, mode: cmd.mode });
    setCmdDialogOpen(true);
  };

  const handleSaveCommand = async () => {
    if (!cmdForm.instruction.trim()) {
      toast.error('Instruction cannot be empty');
      return;
    }
    setCmdSaving(true);
    try {
      if (editingCmd) {
        await updateCommand(editingCmd.id, cmdForm);
        toast.success('Command updated');
      } else {
        await createCommand(cmdForm);
        toast.success('Command created');
      }
      setCmdDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save command');
    } finally {
      setCmdSaving(false);
    }
  };

  const handleToggleCommand = async (id, enabled) => {
    try {
      await toggleCommand(id, enabled);
      setCommands(prev => prev.map(c => c.id === id ? { ...c, enabled } : c));
      toast.success(enabled ? 'Command enabled' : 'Command disabled');
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const handleDeleteCommand = async (id) => {
    try {
      await deleteCommand(id);
      toast.success('Command deleted');
      setCommands(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const engineRunning = status?.engine_running;

  return (
    <div className="p-6 space-y-6" data-testid="outreach-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Outreach Engine</h1>
          <p className="text-muted-foreground mt-1">AI-powered human-like sales outreach</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={() => setConfigOpen(true)} data-testid="outreach-config-btn">
            <Settings2 className="w-4 h-4 mr-2" />Configure
          </Button>
          <div className={cn(
            "flex items-center gap-3 px-4 py-2 rounded-lg border transition-all",
            engineRunning ? "border-emerald-500/40 bg-emerald-500/10" : "border-border/60 bg-muted/20"
          )}>
            <div className={cn("w-2.5 h-2.5 rounded-full", engineRunning ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/40")} />
            <span className={cn("text-sm font-medium", engineRunning ? "text-emerald-400" : "text-muted-foreground")}>
              {engineRunning ? 'Running' : 'Paused'}
            </span>
            <Switch
              checked={engineRunning || false}
              onCheckedChange={handleToggle}
              data-testid="engine-toggle"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{status?.today?.sent || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Sent Today</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{status?.today?.replies || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Replies Today</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{status?.total?.sent || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Outreach</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">
              {status?.total?.sent > 0 ? Math.round((status?.total?.replies / status?.total?.sent) * 100) : 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Reply Rate</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Outreach Queue */}
        <Card className="lg:col-span-2 bg-card/50 border-border/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />Next Up
              </CardTitle>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={loadData}>
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : queue.length === 0 ? (
                <div className="py-8 text-center">
                  <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No candidates</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Sync leads from CRM first</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {queue.map(lead => {
                    const intentCfg = INTENT_CONFIG[lead.intent] || INTENT_CONFIG.first_touch;
                    const stateCfg = STATE_CONFIG[lead.state] || STATE_CONFIG.new;
                    return (
                      <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/15 border border-border/30 hover:bg-muted/25 transition-colors" data-testid={`queue-lead-${lead.id}`}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">{(lead.name || '?')[0]?.toUpperCase()}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{lead.name || 'Unknown'}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", intentCfg.bg, intentCfg.color)}>{intentCfg.label}</span>
                              <span className={cn("text-[10px]", stateCfg.color)}>{stateCfg.label}</span>
                              {lead.outreach_count > 0 && <span className="text-[10px] text-muted-foreground">{lead.outreach_count}x</span>}
                            </div>
                          </div>
                        </div>
                        <TooltipProvider>
                          <Tooltip><TooltipTrigger asChild>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
                              onClick={() => handleSendNow(lead.id)}
                              disabled={sending === lead.id}
                              data-testid={`send-now-${lead.id}`}
                            >
                              {sending === lead.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 text-primary" />}
                            </Button>
                          </TooltipTrigger><TooltipContent>Send Now</TooltipContent></Tooltip>
                        </TooltipProvider>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Activity Log */}
        <Card className="lg:col-span-3 bg-card/50 border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" />Activity Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {logs.length === 0 ? (
                <div className="py-8 text-center">
                  <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No outreach activity yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map(log => {
                    const intentCfg = INTENT_CONFIG[log.intent] || INTENT_CONFIG.first_touch;
                    return (
                      <div key={log.id} className="p-3 rounded-lg bg-muted/10 border border-border/30" data-testid={`log-${log.id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{log.lead_name}</span>
                            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", intentCfg.bg, intentCfg.color)}>{intentCfg.label}</span>
                            <Badge variant="secondary" className={cn("text-[10px]",
                              log.status === 'replied' ? 'text-emerald-400' :
                              log.status === 'sent' ? 'text-blue-400' : 'text-red-400'
                            )}>
                              {log.status}
                            </Badge>
                          </div>
                          <span className="text-[10px] text-muted-foreground">{formatTime(log.created_at)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{log.message}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* ─── Custom Commands Section ──────────────────── */}
      <Card className="bg-card/50 border-border/60" data-testid="custom-commands-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Terminal className="w-4 h-4 text-violet-400" />Custom Commands
            </CardTitle>
            <Button size="sm" onClick={openNewCommand} data-testid="add-command-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />Add Command
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Dynamic instructions that guide AI behavior during outreach conversations</p>
        </CardHeader>
        <CardContent>
          {commands.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-commands-placeholder">
              <Terminal className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No commands yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Add instructions to guide the AI agent's behavior</p>
            </div>
          ) : (
            <div className="space-y-2">
              {commands.map(cmd => (
                <div
                  key={cmd.id}
                  className={cn(
                    "flex items-start justify-between p-3.5 rounded-lg border transition-colors",
                    cmd.enabled ? "bg-muted/15 border-border/40" : "bg-muted/5 border-border/20 opacity-60"
                  )}
                  data-testid={`command-${cmd.id}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge
                        variant="secondary"
                        className={cn(
                          "text-[10px] font-semibold uppercase tracking-wide",
                          cmd.mode === 'active'
                            ? 'bg-red-500/15 text-red-400 border-red-500/30'
                            : 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                        )}
                        data-testid={`command-mode-${cmd.id}`}
                      >
                        {cmd.mode === 'active' ? 'Active Campaign' : 'Passive'}
                      </Badge>
                      {cmd.mode === 'active' && cmd.campaign_running && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Running
                        </span>
                      )}
                      {cmd.sends > 0 && (
                        <span className="text-[10px] text-muted-foreground">{cmd.sends} sent</span>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed">{cmd.instruction}</p>
                    <p className="text-[10px] text-muted-foreground mt-1.5">
                      Created {new Date(cmd.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {cmd.last_sent_at && ` · Last used ${new Date(cmd.last_sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Switch
                      checked={cmd.enabled}
                      onCheckedChange={(val) => handleToggleCommand(cmd.id, val)}
                      data-testid={`command-toggle-${cmd.id}`}
                    />
                    <TooltipProvider>
                      <Tooltip><TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditCommand(cmd)} data-testid={`command-edit-${cmd.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Edit</TooltipContent></Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip><TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDeleteCommand(cmd.id)} data-testid={`command-delete-${cmd.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Delete</TooltipContent></Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Command Create/Edit Dialog */}
      <Dialog open={cmdDialogOpen} onOpenChange={setCmdDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="command-dialog">
          <DialogHeader>
            <DialogTitle>{editingCmd ? 'Edit Command' : 'New Custom Command'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Instruction</Label>
              <Textarea
                placeholder='e.g. "Offer discount if user is hesitant" or "Suggest Telegram channel naturally during conversation"'
                value={cmdForm.instruction}
                onChange={e => setCmdForm(f => ({ ...f, instruction: e.target.value }))}
                rows={4}
                className="mt-1.5"
                data-testid="command-instruction-input"
              />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={cmdForm.mode} onValueChange={v => setCmdForm(f => ({ ...f, mode: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="command-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passive">Passive — Applied only when contextually relevant</SelectItem>
                  <SelectItem value="active">Active Campaign — Actively reaches out to leads</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                {cmdForm.mode === 'passive'
                  ? 'The AI will weave this instruction into messages naturally during regular outreach, only when relevant.'
                  : 'The AI will actively run this as a campaign, reaching out to leads at 5-10 min intervals with this instruction.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCmdDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCommand} disabled={cmdSaving} data-testid="command-save-btn">
              {cmdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCmd ? 'Update' : 'Create'} Command
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="outreach-config-dialog">
          <DialogHeader>
            <DialogTitle>Engine Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min Leads/Day</Label>
                <Input type="number" min={1} max={20} value={config.min_daily_leads || 5} onChange={e => setConfig(c => ({ ...c, min_daily_leads: parseInt(e.target.value) }))} data-testid="config-min-leads" />
              </div>
              <div>
                <Label>Max Leads/Day</Label>
                <Input type="number" min={1} max={20} value={config.max_daily_leads || 10} onChange={e => setConfig(c => ({ ...c, max_daily_leads: parseInt(e.target.value) }))} data-testid="config-max-leads" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Hour (UTC)</Label>
                <Input type="number" min={0} max={23} value={config.day_start_hour || 9} onChange={e => setConfig(c => ({ ...c, day_start_hour: parseInt(e.target.value) }))} data-testid="config-start-hour" />
              </div>
              <div>
                <Label>End Hour (UTC)</Label>
                <Input type="number" min={0} max={23} value={config.day_end_hour || 21} onChange={e => setConfig(c => ({ ...c, day_end_hour: parseInt(e.target.value) }))} data-testid="config-end-hour" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">The engine will randomly select leads and send messages between these hours with randomized delays (2-30 min between sends).</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} data-testid="config-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
